import React from 'react';
import {
    Drawer,
    IconButton,
    Button,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Autocomplete,
    CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Filter, Plus, Trash2, X } from 'lucide-react';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import PearlLogoLoader from 'components/PearlLogoLoader';
import { FilterChip, FilterSelect } from 'components/v2/filters';
import { needsValue } from 'hooks/useKanbanFilters';
import { v2 as v2t, V2_FONT_FAMILY } from 'styles/tokens/v2';

const fieldSx = {
    backgroundColor: '#fff',
    borderRadius: 2,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'info.main' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'info.main', borderWidth: 2 },
};
const textFieldSx = { '& .MuiOutlinedInput-root': fieldSx };

const logicChipSx = (active) => ({
    backgroundColor: active ? 'info.main' : '#e2e8f0',
    color: active ? 'white !important' : '#475569',
    fontWeight: 600,
    fontSize: '0.75rem',
    cursor: 'pointer',
    px: 1,
    '&:hover': { backgroundColor: active ? '#1565c0' : '#cbd5e1' },
});

// UI v2: a small caption above the control instead of MUI's notched floating label.
const V2Caption = ({ label, htmlFor, children }) => (
    <MDBox sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <MDBox
            component="label"
            htmlFor={htmlFor}
            sx={{ fontFamily: V2_FONT_FAMILY, fontSize: 12, fontWeight: 600, color: v2t.text2 }}
        >
            {label}
        </MDBox>
        {children}
    </MDBox>
);

const optionValue = (opt) => (typeof opt === 'object' ? opt.value ?? opt.id : opt);
const optionLabel = (opt) => (typeof opt === 'object' ? opt.label ?? opt.name ?? optionValue(opt) : opt);

/* Renders the right kind of value input for the selected field */
const ConditionValueInput = ({ condition, index, field, onChange, filters }) => {
    const isV2 = useTheme()?.pearlUi === 'v2';
    // Index keeps ids unique when two conditions share a field (amount > X AND amount < Y).
    const valueInputId = `kanban-filter-value-${index}-${condition.field}`;
    const type = field?.type || 'text';

    if (type === 'reference' && field?.reference_to) {
        const refType = field.reference_to;
        const isUsers = refType === 'users';
        const options = isUsers ? filters.users : filters.referenceData[refType] || [];
        const loading = isUsers ? filters.loadingUsers : Boolean(filters.loadingRef[refType]);
        const selected = options.find((o) => String(o.id) === String(condition.value)) || null;
        const v2InputId = isV2 ? valueInputId : undefined;

        const picker = (
            <Autocomplete
                id={v2InputId}
                fullWidth
                size="small"
                options={options}
                loading={loading}
                value={selected}
                getOptionLabel={(o) => o?.label || ''}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                onChange={(_, v) => onChange(v ? String(v.id) : '')}
                onInputChange={(_, text, reason) => {
                    if (isUsers || reason !== 'input') return;
                    if (text.length >= 2) filters.fetchReferenceData(refType, text);
                    else if (text.length === 0) filters.fetchReferenceData(refType, '', true);
                }}
                noOptionsText={loading ? 'Loading...' : `No ${refType} found`}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={isV2 ? undefined : isUsers ? 'Select User' : `Select ${refType.replace(/s$/, '')}`}
                        placeholder={isV2 ? (isUsers ? 'Select user' : `Select ${refType.replace(/s$/, '')}`) : undefined}
                        sx={isV2 ? undefined : textFieldSx}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {loading ? <CircularProgress size={18} /> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
            />
        );
        return isV2 ? (
            <V2Caption label={isUsers ? 'User' : `${refType.charAt(0).toUpperCase()}${refType.slice(1).replace(/s$/, '')}`} htmlFor={v2InputId}>
                {picker}
            </V2Caption>
        ) : (
            picker
        );
    }

    if (type === 'picklist' && (field?.options || []).length > 0) {
        if (isV2) {
            return (
                <FilterSelect
                    label="Value"
                    labelPlacement="above"
                    placeholder="Select value"
                    fullWidth
                    value={condition.value}
                    onChange={onChange}
                    options={field.options.map((opt) => ({ value: optionValue(opt), label: optionLabel(opt) }))}
                />
            );
        }
        return (
            <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '0.875rem' }}>Select Value</InputLabel>
                <Select
                    value={condition.value}
                    label="Select Value"
                    onChange={(e) => onChange(e.target.value)}
                    sx={fieldSx}
                >
                    {field.options.map((opt) => {
                        const val = typeof opt === 'object' ? opt.value ?? opt.id : opt;
                        const label = typeof opt === 'object' ? opt.label ?? opt.name ?? val : opt;
                        return (
                            <MenuItem key={String(val)} value={val}>
                                {label}
                            </MenuItem>
                        );
                    })}
                </Select>
            </FormControl>
        );
    }

    if (isV2 && (type === 'date' || type === 'datetime')) {
        return (
            <V2Caption label="Date" htmlFor={valueInputId}>
                <TextField
                    fullWidth
                    size="small"
                    type="date"
                    id={valueInputId}
                    value={condition.value}
                    onChange={(e) => onChange(e.target.value)}
                />
            </V2Caption>
        );
    }

    if (type === 'date' || type === 'datetime') {
        return (
            <TextField
                fullWidth
                size="small"
                type="date"
                label="Select Date"
                value={condition.value}
                onChange={(e) => onChange(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={textFieldSx}
            />
        );
    }

    const isNumber = type === 'number' || type === 'currency';
    if (isV2) {
        return (
            <V2Caption label="Value" htmlFor={valueInputId}>
                <TextField
                    fullWidth
                    size="small"
                    type={isNumber ? 'number' : 'text'}
                    id={valueInputId}
                    placeholder={isNumber ? 'Enter a number' : 'Type a value...'}
                    value={condition.value}
                    onChange={(e) => onChange(e.target.value)}
                />
            </V2Caption>
        );
    }
    return (
        <TextField
            fullWidth
            size="small"
            type={isNumber ? 'number' : 'text'}
            label="Enter Value"
            placeholder={isNumber ? 'Enter a number' : 'Type a value...'}
            value={condition.value}
            onChange={(e) => onChange(e.target.value)}
            sx={textFieldSx}
        />
    );
};

const KanbanFilterDrawer = ({ filters, objectLabel }) => {
    const isV2 = useTheme()?.pearlUi === 'v2';
    const {
        drawerOpen,
        closeDrawer,
        cancelDrawer,
        editingFilter,
        availableFields,
        loadingFields,
        conditions,
        logicOperator,
        setLogicOperator,
        addCondition,
        removeCondition,
        updateCondition,
        getField,
        getOperatorsForField,
        applyConditions,
        openSaveDialog,
        objectType,
    } = filters;

    const label = objectLabel || (objectType ? `${objectType.charAt(0).toUpperCase()}${objectType.slice(1)}s` : 'Data');

    return (
        <Drawer
            anchor="right"
            open={drawerOpen}
            onClose={closeDrawer}
            PaperProps={{
                sx: {
                    width: { xs: '100%', sm: 480, md: 520 },
                    boxShadow: '-4px 0 20px rgba(0,0,0,0.1)',
                    overflowX: 'hidden',
                },
            }}
        >
            <MDBox sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: '#fff' }}>
                {/* Header */}
                <MDBox
                    sx={{
                        px: 3,
                        py: 2.5,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        backgroundColor: 'info.main',
                    }}
                >
                    <MDBox display="flex" alignItems="center" gap={1.5}>
                        <MDBox
                            sx={{
                                backgroundColor: 'rgba(255,255,255,0.2)',
                                borderRadius: '10px',
                                p: 1,
                                display: 'flex',
                            }}
                        >
                            <Filter size={22} color="#fff" />
                        </MDBox>
                        <MDBox>
                            <MDTypography variant="h6" fontWeight="bold" sx={{ color: '#fff', fontSize: '1.1rem' }}>
                                {editingFilter ? `Edit: ${editingFilter.name}` : `Filter ${label}`}
                            </MDTypography>
                            <MDTypography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                                {availableFields.length} fields available
                            </MDTypography>
                        </MDBox>
                    </MDBox>
                    <IconButton
                        onClick={closeDrawer}
                        size="small"
                        sx={{
                            color: '#fff',
                            backgroundColor: 'rgba(255,255,255,0.1)',
                            '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
                        }}
                    >
                        <X size={20} />
                    </IconButton>
                </MDBox>

                {/* AND / OR */}
                <MDBox
                    sx={{
                        px: 3,
                        py: 2,
                        borderBottom: '1px solid #e8ecf0',
                        backgroundColor: '#f8fafc',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 1.5,
                    }}
                >
                    <MDTypography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
                        Match
                    </MDTypography>
                    {isV2 ? (
                        <>
                            <FilterChip
                                label="All conditions (AND)"
                                selected={logicOperator === 'AND'}
                                onClick={() => setLogicOperator('AND')}
                            />
                            <FilterChip
                                label="Any condition (OR)"
                                selected={logicOperator === 'OR'}
                                onClick={() => setLogicOperator('OR')}
                            />
                        </>
                    ) : (
                        <>
                    <Chip
                        label="All conditions (AND)"
                        size="small"
                        onClick={() => setLogicOperator('AND')}
                        sx={logicChipSx(logicOperator === 'AND')}
                    />
                    <Chip
                        label="Any condition (OR)"
                        size="small"
                        onClick={() => setLogicOperator('OR')}
                        sx={logicChipSx(logicOperator === 'OR')}
                    />
                        </>
                    )}
                </MDBox>

                {/* Conditions */}
                <MDBox sx={{ flexGrow: 1, overflowY: 'auto', px: 3, py: 2.5, backgroundColor: '#f8fafc' }}>
                    {loadingFields ? (
                        <MDBox display="flex" justifyContent="center" py={6}>
                            <PearlLogoLoader size="md" message="Loading fields..." />
                        </MDBox>
                    ) : availableFields.length === 0 ? (
                        <MDBox
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            py={6}
                            sx={{ backgroundColor: '#fff', borderRadius: 3, border: '2px dashed #e2e8f0' }}
                        >
                            <Filter size={44} color="#cbd5e1" />
                            <MDTypography variant="body1" sx={{ mt: 2, color: '#64748b', fontWeight: 500 }}>
                                No fields available
                            </MDTypography>
                            <MDTypography variant="caption" sx={{ color: '#94a3b8', textAlign: 'center', px: 3 }}>
                                {objectType ? `No filterable fields found for ${objectType}` : 'Please select an object type'}
                            </MDTypography>
                        </MDBox>
                    ) : (
                        <>
                            {conditions.map((condition, index) => {
                                const field = getField(condition.field);
                                return (
                                    <MDBox
                                        key={index}
                                        sx={{
                                            mb: 2,
                                            p: 2.5,
                                            border: '1px solid #e2e8f0',
                                            borderRadius: 3,
                                            backgroundColor: '#fff',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                        }}
                                    >
                                        <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                                            <MDBox display="flex" alignItems="center" gap={1}>
                                                <MDBox
                                                    sx={{
                                                        width: 24,
                                                        height: 24,
                                                        borderRadius: '50%',
                                                        backgroundColor: 'info.main',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: '#fff',
                                                        fontSize: '0.75rem',
                                                        fontWeight: 'bold',
                                                    }}
                                                >
                                                    {index + 1}
                                                </MDBox>
                                                <MDTypography variant="body2" fontWeight="medium" sx={{ color: '#334155' }}>
                                                    Condition
                                                </MDTypography>
                                            </MDBox>
                                            {conditions.length > 1 && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => removeCondition(index)}
                                                    sx={{
                                                        color: '#ef4444',
                                                        backgroundColor: '#fef2f2',
                                                        '&:hover': { backgroundColor: '#fee2e2' },
                                                    }}
                                                >
                                                    <Trash2 size={16} />
                                                </IconButton>
                                            )}
                                        </MDBox>

                                        <MDBox sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            {/* Field */}
                                            {isV2 ? (
                                                <FilterSelect
                                                    label="Field"
                                                    labelPlacement="above"
                                                    placeholder="Select field"
                                                    fullWidth
                                                    value={condition.field}
                                                    onChange={(v) => updateCondition(index, 'field', v)}
                                                    options={availableFields.map((f) => ({ value: f.name, label: f.label, hint: f.type }))}
                                                />
                                            ) : (
                                            <FormControl fullWidth size="small">
                                                <InputLabel sx={{ fontSize: '0.875rem' }}>Select Field</InputLabel>
                                                <Select
                                                    value={condition.field}
                                                    label="Select Field"
                                                    onChange={(e) => updateCondition(index, 'field', e.target.value)}
                                                    sx={fieldSx}
                                                    MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                                                >
                                                    {availableFields.map((f) => (
                                                        <MenuItem key={f.name} value={f.name}>
                                                            <MDBox
                                                                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 1 }}
                                                            >
                                                                <MDTypography variant="body2" sx={{ fontWeight: 500, whiteSpace: 'normal' }}>
                                                                    {f.label}
                                                                </MDTypography>
                                                                <Chip
                                                                    label={f.type}
                                                                    size="small"
                                                                    sx={{ fontSize: '0.65rem', height: 20, backgroundColor: '#e3f2fd', color: 'info.main' }}
                                                                />
                                                            </MDBox>
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                            )}

                                            {/* Operator */}
                                            {condition.field && isV2 && (
                                                <FilterSelect
                                                    label="Operator"
                                                    labelPlacement="above"
                                                    placeholder="Select operator"
                                                    fullWidth
                                                    value={condition.operator}
                                                    onChange={(v) => updateCondition(index, 'operator', v)}
                                                    options={getOperatorsForField(condition.field).map((op) => ({ value: op.id, label: op.name }))}
                                                />
                                            )}
                                            {condition.field && !isV2 && (
                                                <FormControl fullWidth size="small">
                                                    <InputLabel sx={{ fontSize: '0.875rem' }}>Select Operator</InputLabel>
                                                    <Select
                                                        value={condition.operator}
                                                        label="Select Operator"
                                                        onChange={(e) => updateCondition(index, 'operator', e.target.value)}
                                                        sx={fieldSx}
                                                    >
                                                        {getOperatorsForField(condition.field).map((op) => (
                                                            <MenuItem key={op.id} value={op.id}>
                                                                {op.name}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            )}

                                            {/* Value */}
                                            {condition.field && condition.operator && needsValue(condition.operator) && (
                                                <ConditionValueInput
                                                    condition={condition}
                                                    index={index}
                                                    field={field}
                                                    filters={filters}
                                                    onChange={(v) => updateCondition(index, 'value', v)}
                                                />
                                            )}
                                        </MDBox>
                                    </MDBox>
                                );
                            })}

                            <Button
                                variant="outlined"
                                startIcon={<Plus size={18} />}
                                onClick={addCondition}
                                fullWidth
                                sx={{
                                    py: 1.25,
                                    borderStyle: 'dashed',
                                    borderWidth: 2,
                                    borderColor: 'info.main',
                                    color: 'info.main',
                                    fontWeight: 600,
                                    borderRadius: 2,
                                    backgroundColor: '#fff',
                                    '&:hover': { borderWidth: 2, borderColor: '#1565c0', backgroundColor: '#e3f2fd' },
                                }}
                            >
                                Add Another Condition
                            </Button>
                        </>
                    )}
                </MDBox>

                {/* Footer */}
                <MDBox
                    sx={{
                        px: 3,
                        py: 2,
                        borderTop: '1px solid #e8ecf0',
                        display: 'flex',
                        gap: 1.5,
                        backgroundColor: '#fff',
                        boxShadow: '0 -4px 12px rgba(0,0,0,0.05)',
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={cancelDrawer}
                        sx={{ flex: 1, borderColor: '#e2e8f0', color: '#64748b', fontWeight: 600, borderRadius: 2 }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={openSaveDialog}
                        disabled={!objectType}
                        sx={{ flex: 1, borderColor: 'info.main', color: 'info.main', fontWeight: 600, borderRadius: 2 }}
                    >
                        {editingFilter ? 'Update' : 'Save'}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={applyConditions}
                        sx={{
                            flex: 1,
                            backgroundColor: 'info.main',
                            color: '#fff !important',
                            fontWeight: 600,
                            borderRadius: 2,
                            boxShadow: 'none',
                            '&:hover': { backgroundColor: '#1565c0', boxShadow: 'none' },
                        }}
                    >
                        Apply
                    </Button>
                </MDBox>
            </MDBox>
        </Drawer>
    );
};

export default KanbanFilterDrawer;